import React from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { useSettings } from '@/contexts/SettingsContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { QUALITY_THRESHOLDS } from '@/constants/analyticsThresholds';

const TOP_N_OPTIONS = [25, 50, 100, 200] as const;

export default function AnalyticsSettingsPage() {
  const { settings, updateAnalyticsDefaultTopN, updateAnalyticsShowLabels } = useSettings();

  return (
    <div className="flex flex-col h-full min-h-0">
      <PageHeader
        title="Analytics Settings"
        subtitle="Project defaults and your analytics presentation preferences."
      />

      <div className="space-y-6 pt-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Project defaults</CardTitle>
            <p className="text-sm text-muted-foreground">
              Thresholds used for schedule health and quality calculations. To change project-level
              thresholds, contact your administrator.
            </p>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div>
              <h4 className="font-medium text-foreground mb-2">Schedule health</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Negative float: OK=0, Warning 1–5, Critical &gt;5</li>
                <li>Critical/near-critical (float ≤5d): OK ≤10, Warning 11–25, Critical &gt;25</li>
                <li>High float % (&gt;44d): OK ≤15%, Warning 16–30%, Critical &gt;30%</li>
                <li>Finish delta: OK ≤0, Warning 1–14d, Critical &gt;14d</li>
                <li>Min total float: OK ≥0, Warning (-7,0), Critical ≤-7d</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-foreground mb-2">Quality</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>High float %: Pass ≤15%, Warn 16–30%, Fail &gt;30%</li>
                <li>Long duration % (&gt;44d): Pass ≤10%, Warn 11–20%, Fail &gt;20%</li>
                <li>Missing relations: Pass=0, Warn 1–3, Fail &gt;3</li>
                <li>Constraints: Pass ≤{QUALITY_THRESHOLDS.constraintsCount.passMax}</li>
                <li>Excessive lags: Pass ≤{QUALITY_THRESHOLDS.excessiveLags.passMax}</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">User preferences</CardTitle>
            <p className="text-sm text-muted-foreground">
              Presentation settings for analytics charts. Stored in your browser.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Default Top N for ranked charts
              </label>
              <Select
                value={String(settings.analyticsDefaultTopN)}
                onValueChange={(v) => updateAnalyticsDefaultTopN(Number(v))}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TOP_N_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Applied when opening WBS Hotspots or Milestone Variance in focus view.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="analytics-show-labels"
                checked={settings.analyticsShowLabels}
                onCheckedChange={(checked) =>
                  updateAnalyticsShowLabels(checked === true)
                }
              />
              <label
                htmlFor="analytics-show-labels"
                className="text-sm font-medium text-foreground cursor-pointer"
              >
                Show labels on chart by default
              </label>
            </div>
            <p className="text-xs text-muted-foreground">
              When enabled, value labels are shown on bar charts in focus view. You can toggle this
              per chart.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
